/**
 * The ladder's door to the knock-on review (#1964, slice 3D of #1947).
 *
 * A row of the "Open this seat to" prompt whose person is not free only
 * because they sit on one other ward that session carries `holds_seat`
 * (`56 AM`). Its button reads **Seat <name> · moves them**; a click previews
 * the seat (`POST …/shortfalls/seat/preview`) and hands the draft to
 * `<SeatKnockOnHost/>`, which opens the shared review screen in the
 * right-panel overlay.
 *
 * An API before #1964 has no `holds_seat`, so the row stays the disabled one
 * with its reason. A preview that 404s (the route is not there) turns every
 * such row back into that disabled row for the rest of the panel's life.
 */
import { useCallback, useState } from "react";

import { ApiError } from "../../api/client";
import { type SeatTarget, usePreviewSeat } from "../../api/useSeatKnockOn";
import { useSeatKnockOnStore } from "../../store/seatKnockOnRequest";

export interface SeatKnockOnLauncher {
  /** Seating this person moves them off the ward they sit on. */
  canMove: (person: { holds_seat?: string | null | undefined }) => boolean;
  /** Preview the seat and open the review. Rejects with the API error. */
  launch: (rowKey: string, target: SeatTarget, displayName: string) => Promise<void>;
  /** The row whose preview is in flight. */
  loadingKey: string | null;
}

export function useSeatKnockOnLauncher(cycleId: string): SeatKnockOnLauncher {
  const preview = usePreviewSeat(cycleId);
  const open = useSeatKnockOnStore((s) => s.openSeatKnockOn);
  const [unsupported, setUnsupported] = useState(false);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const canMove = useCallback(
    (person: { holds_seat?: string | null | undefined }) =>
      !unsupported && Boolean(person.holds_seat),
    [unsupported],
  );

  const launch = useCallback(
    async (rowKey: string, target: SeatTarget, displayName: string) => {
      setLoadingKey(rowKey);
      try {
        const draft = await preview.mutateAsync(target);
        open({ cycleId, target, displayName, preview: draft });
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setUnsupported(true);
          return;
        }
        throw err;
      } finally {
        setLoadingKey(null);
      }
    },
    [cycleId, open, preview],
  );

  return { canMove, launch, loadingKey };
}
